/**
 * Settings Panel
 *
 * Full-page settings interface with sidebar navigation.
 * Includes app settings (profile, theme), Claude Code settings, security info, and more.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAnimatedMount, ANIMATION_DURATION } from '@/hooks/useAnimatedMount';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BrandLogo } from '@/components/ui/BrandLogo';
import { useSettingsStore, Theme } from '@/stores/settingsStore';
import { useAuthStore } from '@/stores/authStore';
import { api, SystemStatus, AdminUser, TrainingMemory, TrainingMemoryCategory } from '@/services/api';
import { ClaudeAuthPanel } from '@/components/ClaudeAuthPanel';
import { CodexAuthPanel } from '@/components/CodexAuthPanel';
import { SwitchConnectorPanel } from '@/components/SwitchConnectorPanel';
import { BrowserTraceCapturePanel } from '@/components/browser-traces';
import { LearningPanel } from '@/components/LearningPanel';
import {
  X,
  Moon,
  Sun,
  Monitor,
  User,
  Link2,
  Settings2,
  Info,
  Check,
  Shield,
  Lock,
  Server,
  HardDrive,
  Eye,
  Keyboard,
  ExternalLink,
  BookOpen,
  CheckCircle,
  XCircle,
  Loader2,
  FolderOpen,
  Users,
  Plus,
  Trash2,
  Key,
  AlertCircle,
  Sparkles,
  Upload,
  RotateCw,
  RefreshCw,
  ChevronDown,
  Search,
} from 'lucide-react';

type SettingsSection = 'app' | 'claude' | 'switches' | 'traces' | 'security' | 'about' | 'users' | 'training' | 'skills';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  initialSection?: SettingsSection;
}

interface TimezoneOption {
  id: string;
  value: string;
  label: string;
  city: string;
  region: string;
  offset: string;
  search: string;
}

type IntlWithTimezones = typeof Intl & {
  supportedValuesOf?: (key: 'timeZone') => string[];
};

const FALLBACK_TIMEZONES = [
  'UTC',
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Toronto',
  'America/Mexico_City',
  'America/Bogota',
  'America/Lima',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Atlantic/Reykjavik',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Europe/Athens',
  'Europe/Istanbul',
  'Africa/Casablanca',
  'Africa/Lagos',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Asia/Jerusalem',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Sydney',
  'Pacific/Auckland',
];

const US_CITY_TIMEZONES = [
  { city: 'Anchorage', state: 'AK', timezone: 'America/Anchorage' },
  { city: 'Atlanta', state: 'GA', timezone: 'America/New_York' },
  { city: 'Austin', state: 'TX', timezone: 'America/Chicago' },
  { city: 'Baltimore', state: 'MD', timezone: 'America/New_York' },
  { city: 'Boston', state: 'MA', timezone: 'America/New_York' },
  { city: 'Charlotte', state: 'NC', timezone: 'America/New_York' },
  { city: 'Chicago', state: 'IL', timezone: 'America/Chicago' },
  { city: 'Cincinnati', state: 'OH', timezone: 'America/New_York' },
  { city: 'Cleveland', state: 'OH', timezone: 'America/New_York' },
  { city: 'Columbus', state: 'OH', timezone: 'America/New_York' },
  { city: 'Dallas', state: 'TX', timezone: 'America/Chicago' },
  { city: 'Denver', state: 'CO', timezone: 'America/Denver' },
  { city: 'Detroit', state: 'MI', timezone: 'America/Detroit' },
  { city: 'Houston', state: 'TX', timezone: 'America/Chicago' },
  { city: 'Honolulu', state: 'HI', timezone: 'Pacific/Honolulu' },
  { city: 'Indianapolis', state: 'IN', timezone: 'America/Indiana/Indianapolis' },
  { city: 'Jacksonville', state: 'FL', timezone: 'America/New_York' },
  { city: 'Kansas City', state: 'MO', timezone: 'America/Chicago' },
  { city: 'Las Vegas', state: 'NV', timezone: 'America/Los_Angeles' },
  { city: 'Los Angeles', state: 'CA', timezone: 'America/Los_Angeles' },
  { city: 'Louisville', state: 'KY', timezone: 'America/Kentucky/Louisville' },
  { city: 'Memphis', state: 'TN', timezone: 'America/Chicago' },
  { city: 'Miami', state: 'FL', timezone: 'America/New_York' },
  { city: 'Milwaukee', state: 'WI', timezone: 'America/Chicago' },
  { city: 'Minneapolis', state: 'MN', timezone: 'America/Chicago' },
  { city: 'Nashville', state: 'TN', timezone: 'America/Chicago' },
  { city: 'New Orleans', state: 'LA', timezone: 'America/Chicago' },
  { city: 'New York', state: 'NY', timezone: 'America/New_York' },
  { city: 'Orlando', state: 'FL', timezone: 'America/New_York' },
  { city: 'Philadelphia', state: 'PA', timezone: 'America/New_York' },
  { city: 'Phoenix', state: 'AZ', timezone: 'America/Phoenix' },
  { city: 'Pittsburgh', state: 'PA', timezone: 'America/New_York' },
  { city: 'Portland', state: 'OR', timezone: 'America/Los_Angeles' },
  { city: 'Raleigh', state: 'NC', timezone: 'America/New_York' },
  { city: 'Sacramento', state: 'CA', timezone: 'America/Los_Angeles' },
  { city: 'Salt Lake City', state: 'UT', timezone: 'America/Denver' },
  { city: 'San Antonio', state: 'TX', timezone: 'America/Chicago' },
  { city: 'San Diego', state: 'CA', timezone: 'America/Los_Angeles' },
  { city: 'San Francisco', state: 'CA', timezone: 'America/Los_Angeles' },
  { city: 'San Jose', state: 'CA', timezone: 'America/Los_Angeles' },
  { city: 'Seattle', state: 'WA', timezone: 'America/Los_Angeles' },
  { city: 'St. Louis', state: 'MO', timezone: 'America/Chicago' },
  { city: 'Tampa', state: 'FL', timezone: 'America/New_York' },
  { city: 'Washington', state: 'DC', timezone: 'America/New_York' },
] as const;

function getSupportedTimezones(): string[] {
  const browserTimezones = (Intl as IntlWithTimezones).supportedValuesOf?.('timeZone') || [];
  const zones = browserTimezones.length > 0
    ? [...browserTimezones, ...US_CITY_TIMEZONES.map((city) => city.timezone)]
    : [...FALLBACK_TIMEZONES, ...US_CITY_TIMEZONES.map((city) => city.timezone)];
  return Array.from(new Set(['UTC', ...zones])).sort((a, b) => a.localeCompare(b));
}

function formatTimezoneOffset(timezone: string): string {
  if (timezone === 'UTC') return 'UTC+00:00';

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date());

    const offset = parts.find((part) => part.type === 'timeZoneName')?.value;
    if (!offset || offset === 'GMT') return 'UTC+00:00';
    return offset.replace('GMT', 'UTC');
  } catch {
    return '';
  }
}

function getTimezoneCity(timezone: string): string {
  if (timezone === 'UTC') return 'UTC';
  const segments = timezone.split('/');
  return (segments[segments.length - 1] || timezone).replace(/_/g, ' ');
}

function getTimezoneRegion(timezone: string): string {
  if (timezone === 'UTC') return 'Global';
  return timezone.split('/')[0]?.replace(/_/g, ' ') || 'Global';
}

function buildTimezoneOptions(): TimezoneOption[] {
  const canonicalOptions = getSupportedTimezones().map((timezone) => {
    const city = getTimezoneCity(timezone);
    const region = getTimezoneRegion(timezone);
    const offset = formatTimezoneOffset(timezone);
    const location = timezone === 'UTC' ? 'UTC' : `${city}, ${region}`;
    const label = offset ? `${location} (${offset})` : location;

    return {
      id: `tz:${timezone}`,
      value: timezone,
      label,
      city,
      region,
      offset,
      search: `${label} ${timezone}`.toLowerCase(),
    };
  });

  const usCityOptions = US_CITY_TIMEZONES.filter((city) => (
    getTimezoneCity(city.timezone).toLowerCase() !== city.city.toLowerCase()
  )).map((city) => {
    const offset = formatTimezoneOffset(city.timezone);
    const label = offset ? `${city.city}, ${city.state} (${offset})` : `${city.city}, ${city.state}`;

    return {
      id: `us:${city.city}:${city.state}:${city.timezone}`,
      value: city.timezone,
      label,
      city: city.city,
      region: `United States (${city.state})`,
      offset,
      search: `${label} ${city.timezone} ${city.city} ${city.state} United States USA US`.toLowerCase(),
    };
  });

  return [...canonicalOptions, ...usCityOptions].sort((a, b) => {
    const citySort = a.city.localeCompare(b.city);
    if (citySort !== 0) return citySort;
    return a.region.localeCompare(b.region);
  });
}

const TIMEZONE_OPTIONS = buildTimezoneOptions();

function fuzzyMatchesText(text: string, query: string): boolean {
  let cursor = 0;
  for (const char of query) {
    const nextIndex = text.indexOf(char, cursor);
    if (nextIndex === -1) return false;
    cursor = nextIndex + 1;
  }

  return true;
}

function scoreTimezoneOption(option: TimezoneOption, query: string): number {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return 1;

  const city = option.city.toLowerCase();
  const label = option.label.toLowerCase();
  const value = option.value.toLowerCase();
  const region = option.region.toLowerCase();
  const searchable = option.search;

  const queryWords = normalizedQuery.split(/\s+/).filter(Boolean);
  const everyWordMatches = queryWords.every((word) => (
    searchable.includes(word) ||
    (word.length <= 4 && fuzzyMatchesText(searchable, word))
  ));
  if (!everyWordMatches) return 0;

  if (city === normalizedQuery) return 100;
  if (label.startsWith(normalizedQuery)) return 90;
  if (city.startsWith(normalizedQuery)) return 85;
  if (value.includes(normalizedQuery)) return 70;
  if (region.includes(normalizedQuery)) return 55;
  if (label.includes(normalizedQuery)) return 50;
  if (queryWords.length > 1) return 40;
  if (normalizedQuery.length <= 4 && fuzzyMatchesText(searchable, normalizedQuery)) return 20;

  return 10;
}

function TimezoneSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (timezone: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const selectedOption =
    TIMEZONE_OPTIONS.find((option) => option.value === value && option.id.startsWith('tz:')) ||
    TIMEZONE_OPTIONS.find((option) => option.value === value) || {
      id: `custom:${value}`,
      value,
      label: value || 'Select timezone',
      city: value,
      region: 'Custom',
      offset: '',
      search: value.toLowerCase(),
    };

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return TIMEZONE_OPTIONS.slice(0, 80);

    return TIMEZONE_OPTIONS
      .map((option) => ({
        option,
        score: scoreTimezoneOption(option, normalizedQuery),
      }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const citySort = a.option.city.localeCompare(b.option.city);
        if (citySort !== 0) return citySort;
        return a.option.region.localeCompare(b.option.region);
      })
      .map(({ option }) => option)
      .slice(0, 80);
  }, [query]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const selectTimezone = (timezone: string) => {
    onChange(timezone);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
        <Input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="timezone-options"
          aria-autocomplete="list"
          autoComplete="off"
          value={open ? query : selectedOption.label}
          onFocus={() => {
            setOpen(true);
            setQuery('');
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              setQuery('');
            }
            if (event.key === 'Enter' && open && filteredOptions[0]) {
              event.preventDefault();
              selectTimezone(filteredOptions[0].value);
            }
          }}
          placeholder="Search city or timezone"
          className="mt-1.5 pr-10 pl-9"
        />
        <button
          type="button"
          onClick={() => {
            setOpen((current) => !current);
            setQuery('');
          }}
          aria-label="Open timezone list"
          className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-surface-400 hover:bg-surface-100 hover:text-surface-600 dark:hover:bg-surface-800 dark:hover:text-surface-200"
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </button>
      </div>

      {open && (
        <div
          id="timezone-options"
          role="listbox"
          className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-surface-200 bg-white py-1 shadow-lg dark:border-surface-700 dark:bg-surface-900"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={option.id === selectedOption.id}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectTimezone(option.value)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors',
                  option.id === selectedOption.id
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/25 dark:text-primary-300'
                    : 'text-surface-700 hover:bg-surface-50 dark:text-surface-200 dark:hover:bg-surface-800'
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{option.city}</span>
                  <span className="block truncate text-xs text-surface-500 dark:text-surface-400">
                    {option.value}{option.offset ? ` · ${option.offset}` : ''}
                  </span>
                </span>
                {option.id === selectedOption.id && <Check className="h-4 w-4 shrink-0" />}
              </button>
            ))
          ) : (
            <div className="px-3 py-6 text-center text-sm text-surface-500 dark:text-surface-400">
              No timezone matches.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// TelcoOS Logo Component
function TelcoOSLogo({ className }: { className?: string }) {
  return <BrandLogo className={className} />;
}

export function SettingsPanel({ isOpen, onClose, initialSection }: SettingsPanelProps) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<SettingsSection>(initialSection || 'app');
  const [claudeStatus, setClaudeStatus] = useState<'checking' | 'installed' | 'not-found'>('checking');
  const [codexStatus, setCodexStatus] = useState<'checking' | 'installed' | 'not-found'>('checking');
  const [codexStatusError, setCodexStatusError] = useState<string | null>(null);
  const [systemInfo, setSystemInfo] = useState<{
    dataDir?: string;
    claudeVersion?: string;
    claudeRuntime?: 'cli' | 'agent_sdk';
    claudeAvailable?: boolean;
  } | null>(null);

  // User management state (admin only)
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newUserIsAdmin, setNewUserIsAdmin] = useState(false);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserError, setCreateUserError] = useState<string | null>(null);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<number | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [deleteConfirmUserId, setDeleteConfirmUserId] = useState<number | null>(null);
  const [resetClaudeCredsUserId, setResetClaudeCredsUserId] = useState<number | null>(null);
  const [resetClaudeCredsLoading, setResetClaudeCredsLoading] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);

  const [trainingMemories, setTrainingMemories] = useState<TrainingMemory[]>([]);
  const [trainingEvents, setTrainingEvents] = useState<string[]>([]);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingError, setTrainingError] = useState<string | null>(null);
  const [trainingTitle, setTrainingTitle] = useState('');
  const [trainingContent, setTrainingContent] = useState('');
  const [trainingCategory, setTrainingCategory] = useState<TrainingMemoryCategory>('role');
  const [trainingAppliesWhen, setTrainingAppliesWhen] = useState('');
  const [trainingSaving, setTrainingSaving] = useState(false);

  // Auth wizard state (for layout expansion)
  const [isClaudeWizardActive, setIsClaudeWizardActive] = useState(false);
  const [isCodexWizardActive, setIsCodexWizardActive] = useState(false);
  const isIntegrationWizardActive = isClaudeWizardActive || isCodexWizardActive;

  // Self-service password change state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPasswordSelf, setNewPasswordSelf] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);

  // Skills management state (admin only)
  interface SharedSkill {
    name: string;
    title: string;
    description: string;
    shareable?: boolean;
  }
  const [sharedSkills, setSharedSkills] = useState<SharedSkill[]>([]);
  const [installedSkillNames, setInstalledSkillNames] = useState<Set<string>>(new Set());
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillsError, setSkillsError] = useState<string | null>(null);
  const [uploadingSkill, setUploadingSkill] = useState(false);
  const [installingSkill, setInstallingSkill] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteSkillConfirm, setDeleteSkillConfirm] = useState<string | null>(null);
  const [expandedSkillDescriptions, setExpandedSkillDescriptions] = useState<Set<string>>(new Set());

  const settings = useSettingsStore();
  const { user } = useAuthStore();
  const isAdmin = user?.isAdmin || false;
  const legacyTrainingMemoryEnabled = false;

  // Set active section when initialSection changes or modal opens
  useEffect(() => {
    if (isOpen && initialSection) {
      setActiveSection(initialSection);
    }
  }, [isOpen, initialSection]);

  // Check AI connection status and system info
  useEffect(() => {
    if (isOpen) {
      checkAiConnections();
      fetchSystemInfo();
      if (isAdmin) {
        fetchUsers();
        fetchSharedSkills();
        fetchTrainingMemory();
      }
    }
    // Run when the modal opens or admin-only sections become available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAdmin]);

  const checkClaudeInstallation = async () => {
    setClaudeStatus('checking');
    try {
      const response: SystemStatus = await api.system.getStatus();
      if (response.claude?.available) {
        setClaudeStatus('installed');
      } else {
        setClaudeStatus('not-found');
      }
    } catch {
      setClaudeStatus('not-found');
    }
  };

  const checkCodexInstallation = async () => {
    setCodexStatus('checking');
    setCodexStatusError(null);
    try {
      const response = await api.codex.getStatus();
      setCodexStatus(response.cliInstalled ? 'installed' : 'not-found');
      setCodexStatusError(response.error || null);
    } catch (err) {
      setCodexStatus('not-found');
      setCodexStatusError(err instanceof Error ? err.message : 'Failed to check Codex status');
    }
  };

  const checkAiConnections = () => {
    void Promise.all([checkClaudeInstallation(), checkCodexInstallation()]);
  };

  const fetchSystemInfo = async () => {
    try {
      const response: SystemStatus = await api.system.getStatus();
      setSystemInfo({
        dataDir: response.dataDirectory?.path,
        claudeVersion: response.claude?.version,
        claudeRuntime: response.claude?.runtime,
        claudeAvailable: response.claude?.available,
      });
    } catch {
      setSystemInfo(null);
    }
  };

  // User management functions (admin only)
  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const response = await api.admin.getUsers();
      setUsers(response.users);
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchSharedSkills = async () => {
    setSkillsLoading(true);
    setSkillsError(null);
    try {
      const [sharedResponse, installedResponse] = await Promise.all([
        api.skills.listShared(),
        api.skills.list(),
      ]);
      setSharedSkills(sharedResponse.skills);
      setInstalledSkillNames(new Set(installedResponse.skills.map((skill) => skill.name)));
    } catch (err) {
      setSkillsError(err instanceof Error ? err.message : 'Failed to load skills');
    } finally {
      setSkillsLoading(false);
    }
  };

  const handleInstallSkillForMe = async (skillName: string) => {
    setInstallingSkill(skillName);
    setSkillsError(null);
    const alreadyInstalled = installedSkillNames.has(skillName);

    try {
      await api.skills.install(skillName);
      const installedResponse = await api.skills.list();
      setInstalledSkillNames(new Set(installedResponse.skills.map((skill) => skill.name)));
      toast.success(alreadyInstalled ? 'Skill updated' : 'Skill installed', {
        description: alreadyInstalled
          ? 'Your copy was refreshed from the shared source.'
          : 'Start a new chat to use this skill.',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Install failed';
      setSkillsError(message);
      toast.error(message);
    } finally {
      setInstallingSkill(null);
    }
  };

  const toggleSkillDescription = (skillName: string) => {
    setExpandedSkillDescriptions((current) => {
      const next = new Set(current);
      if (next.has(skillName)) {
        next.delete(skillName);
      } else {
        next.add(skillName);
      }
      return next;
    });
  };

  const handleCreateUser = async () => {
    if (!newUsername.trim() || !newPassword) return;
    setCreateUserLoading(true);
    setCreateUserError(null);
    try {
      await api.admin.createUser({
        username: newUsername.trim(),
        password: newPassword,
        isAdmin: newUserIsAdmin,
      });
      setNewUsername('');
      setNewPassword('');
      setNewUserIsAdmin(false);
      setShowCreateUser(false);
      fetchUsers();
    } catch (err) {
      setCreateUserError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setCreateUserLoading(false);
    }
  };

  const handleDeleteUser = async (id: number) => {
    try {
      await api.admin.deleteUser(id);
      setDeleteConfirmUserId(null);
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  const handleResetPassword = async (id: number) => {
    if (!resetPassword) return;
    setResetPasswordLoading(true);
    setResetPasswordError(null);
    try {
      await api.admin.resetPassword(id, resetPassword);
      setResetPasswordUserId(null);
      setResetPassword('');
    } catch (err) {
      console.error('Failed to reset password:', err);
      setResetPasswordError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleResetClaudeCredentials = async (id: number) => {
    setResetClaudeCredsLoading(true);
    try {
      await api.admin.resetClaudeCredentials(id);
      setResetClaudeCredsUserId(null);
    } catch (err) {
      console.error('Failed to reset Claude credentials:', err);
    } finally {
      setResetClaudeCredsLoading(false);
    }
  };

  const fetchTrainingMemory = async () => {
    setTrainingLoading(true);
    setTrainingError(null);
    try {
      const response = await api.admin.getTrainingMemory();
      setTrainingMemories(response.memories);
      setTrainingEvents(response.events.slice(0, 8).map((event) => event.summary));
    } catch (err) {
      setTrainingError(err instanceof Error ? err.message : 'Failed to load Training Memory');
    } finally {
      setTrainingLoading(false);
    }
  };

  const handleCreateTrainingMemory = async () => {
    setTrainingSaving(true);
    setTrainingError(null);
    try {
      const appliesWhen = trainingAppliesWhen
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);
      await api.admin.createTrainingMemory({
        title: trainingTitle,
        content: trainingContent,
        category: trainingCategory,
        appliesWhen,
      });
      setTrainingTitle('');
      setTrainingContent('');
      setTrainingAppliesWhen('');
      setTrainingCategory('role');
      await fetchTrainingMemory();
      toast.success('Training Memory added');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create Training Memory';
      setTrainingError(message);
      toast.error(message);
    } finally {
      setTrainingSaving(false);
    }
  };

  const handleTrainingMemoryStatus = async (id: string, status: 'active' | 'archived') => {
    setTrainingError(null);
    try {
      await api.admin.updateTrainingMemoryStatus(id, status);
      await fetchTrainingMemory();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update Training Memory';
      setTrainingError(message);
      toast.error(message);
    }
  };

  const handleMaterializeTrainingMemory = async () => {
    setTrainingError(null);
    try {
      const result = await api.admin.materializeTrainingMemory();
      await fetchTrainingMemory();
      toast.success('Knowledge bundle materialized', {
        description: `${result.activeCount} active memories written`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to materialize Training Memory';
      setTrainingError(message);
      toast.error(message);
    }
  };

  const handleChangePassword = async () => {
    setChangePasswordError(null);
    setChangePasswordSuccess(false);

    // Validate
    if (!currentPassword) {
      setChangePasswordError('Current password is required');
      return;
    }
    if (!newPasswordSelf || newPasswordSelf.length < 8) {
      setChangePasswordError('New password must be at least 8 characters');
      return;
    }
    if (newPasswordSelf !== confirmNewPassword) {
      setChangePasswordError('Passwords do not match');
      return;
    }

    setChangePasswordLoading(true);
    try {
      await api.auth.changePassword(currentPassword, newPasswordSelf);
      setChangePasswordSuccess(true);
      setCurrentPassword('');
      setNewPasswordSelf('');
      setConfirmNewPassword('');
      // Hide form after 2 seconds
      setTimeout(() => {
        setShowChangePassword(false);
        setChangePasswordSuccess(false);
      }, 2000);
    } catch (err) {
      setChangePasswordError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const { shouldRender, isVisible } = useAnimatedMount(isOpen, {
    exitDuration: ANIMATION_DURATION.normal,
    enterDuration: ANIMATION_DURATION.normal,
  });

  if (!shouldRender) return null;

  const sections: { id: SettingsSection; label: string; icon: React.ReactNode; adminOnly?: boolean }[] = [
    { id: 'app', label: 'App Settings', icon: <Settings2 className="w-4 h-4" /> },
    { id: 'claude', label: 'AI Connections', icon: <Link2 className="w-4 h-4" /> },
    { id: 'switches', label: 'Switch Connections', icon: <Server className="w-4 h-4" /> },
    { id: 'traces', label: 'Browser Traces', icon: <ExternalLink className="w-4 h-4" /> },
    { id: 'users', label: 'Users', icon: <Users className="w-4 h-4" />, adminOnly: true },
    { id: 'training', label: 'Learning', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'skills', label: 'Skills', icon: <Sparkles className="w-4 h-4" />, adminOnly: true },
    { id: 'security', label: 'Security', icon: <Shield className="w-4 h-4" /> },
    { id: 'about', label: 'About', icon: <Info className="w-4 h-4" /> },
  ];

  // Filter sections based on admin status
  const visibleSections = sections.filter(s => !s.adminOnly || isAdmin);

  const themeOptions: { value: Theme; label: string; icon: React.ReactNode }[] = [
    { value: 'light', label: 'Light', icon: <Sun className="w-5 h-5" /> },
    { value: 'dark', label: 'Dark', icon: <Moon className="w-5 h-5" /> },
    { value: 'system', label: 'System', icon: <Monitor className="w-5 h-5" /> },
  ];

  const shortcuts = [
    { keys: ['Cmd', 'K'], description: 'Focus chat input' },
    { keys: ['Cmd', ','], description: 'Open settings' },
    { keys: ['Ctrl', '`'], description: 'Toggle terminal' },
    { keys: ['Esc'], description: 'Close modals' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/60 z-50',
          'transition-opacity duration-150',
          isVisible ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
      />

      {/* Settings Panel */}
      <div
        className={cn(
          'fixed inset-4 md:inset-10 lg:inset-20 bg-white dark:bg-surface-900 rounded-xl shadow-lg z-50 flex overflow-hidden border border-surface-200 dark:border-surface-700',
          'transition-all duration-150',
          isVisible
            ? 'opacity-100 scale-100'
            : 'opacity-0 scale-[0.98]'
        )}
      >
        {/* Sidebar */}
        <div className="w-56 bg-surface-50 dark:bg-surface-800 border-r border-surface-200 dark:border-surface-700 flex flex-col">
          <div className="h-14 px-4 flex items-center border-b border-surface-200 dark:border-surface-700">
            <h2 className="text-lg font-semibold text-surface-900 dark:text-white">Settings</h2>
          </div>
          <nav className="flex-1 p-2">
            {visibleSections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors relative',
                  activeSection === section.id
                    ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                    : 'text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700'
                )}
              >
                {section.icon}
                {section.label}
              </button>
            ))}
          </nav>
          <div className="p-4 border-t border-surface-200 dark:border-surface-700">
            <p className="text-xs text-surface-500 dark:text-surface-400">TelcoOS v0.1.0</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="h-14 px-6 flex items-center justify-between border-b border-surface-200 dark:border-surface-700">
            <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
              {visibleSections.find((s) => s.id === activeSection)?.label}
            </h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-surface-500 dark:text-surface-400"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {/* Responsive two-column grid container */}
            <div className="grid grid-cols-1 gap-6 items-start lg:grid-cols-2">
            {/* AI Connections Section */}
            {activeSection === 'claude' && (
              <div className="lg:col-span-2 space-y-6">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {!isIntegrationWizardActive && (
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">
                        CLI Availability
                      </label>
                      <div className="grid gap-3 rounded-lg border border-surface-200 bg-surface-50 p-3 dark:border-surface-700 dark:bg-surface-900 lg:grid-cols-[1fr_1fr_auto]">
                        <div className="flex items-start gap-3 rounded-md bg-white p-3 dark:bg-surface-800">
                          {claudeStatus === 'checking' && <Loader2 className="mt-0.5 h-5 w-5 animate-spin text-primary-500" />}
                          {claudeStatus === 'installed' && <CheckCircle className="mt-0.5 h-5 w-5 text-primary-600 dark:text-primary-400" />}
                          {claudeStatus === 'not-found' && <XCircle className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" />}
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-surface-900 dark:text-surface-100">
                              Claude Code
                            </div>
                            <div className={cn(
                              'mt-0.5 text-xs',
                              claudeStatus === 'installed'
                                ? 'text-primary-700 dark:text-primary-300'
                                : claudeStatus === 'not-found'
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-surface-500 dark:text-surface-400'
                            )}>
                              {claudeStatus === 'checking' && 'Checking availability...'}
                              {claudeStatus === 'installed' && (
                                <>
                                  Available
                                  {systemInfo?.claudeVersion ? ` · ${systemInfo.claudeVersion}` : ''}
                                  {systemInfo?.claudeRuntime ? ` · Runtime: ${systemInfo.claudeRuntime === 'agent_sdk' ? 'Agent SDK' : 'CLI'}` : ''}
                                </>
                              )}
                              {claudeStatus === 'not-found' && 'Not detected'}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start gap-3 rounded-md bg-white p-3 dark:bg-surface-800">
                          {codexStatus === 'checking' && <Loader2 className="mt-0.5 h-5 w-5 animate-spin text-primary-500" />}
                          {codexStatus === 'installed' && <CheckCircle className="mt-0.5 h-5 w-5 text-primary-600 dark:text-primary-400" />}
                          {codexStatus === 'not-found' && <XCircle className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" />}
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-surface-900 dark:text-surface-100">
                              Codex CLI
                            </div>
                            <div className={cn(
                              'mt-0.5 text-xs',
                              codexStatus === 'installed'
                                ? 'text-primary-700 dark:text-primary-300'
                                : codexStatus === 'not-found'
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-surface-500 dark:text-surface-400'
                            )}>
                              {codexStatus === 'checking' && 'Checking availability...'}
                              {codexStatus === 'installed' && 'Available'}
                              {codexStatus === 'not-found' && (codexStatusError || 'Not detected')}
                            </div>
                          </div>
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={checkAiConnections}
                          className="self-center lg:ml-1"
                        >
                          Check Status
                        </Button>
                      </div>
                    </div>
                  )}

                  {!isCodexWizardActive && (
                    <div className={cn(isClaudeWizardActive && 'lg:col-span-2')}>
                      {!isIntegrationWizardActive && (
                        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">
                          Claude Account
                        </label>
                      )}
                      <ClaudeAuthPanel onWizardStateChange={setIsClaudeWizardActive} />
                    </div>
                  )}

                  {!isClaudeWizardActive && (
                    <div className={cn(isCodexWizardActive && 'lg:col-span-2')}>
                      {!isIntegrationWizardActive && (
                        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">
                          Codex Account
                        </label>
                      )}
                      <CodexAuthPanel onWizardStateChange={setIsCodexWizardActive} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Switch Connections Section */}
            {activeSection === 'switches' && (
              <div className="lg:col-span-2 space-y-6">
                <SwitchConnectorPanel
                  isAdmin={isAdmin}
                  isSupportAdmin={Boolean(user?.isSupportAdmin)}
                />
              </div>
            )}

            {/* Browser Traces Section */}
            {activeSection === 'traces' && (
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-1">
                    Browser Traces
                  </h3>
                  <p className="text-sm text-surface-500 dark:text-surface-400">
                    Capture redacted HAR files for switch adapter discovery.
                  </p>
                </div>

                <BrowserTraceCapturePanel />
              </div>
            )}

            {/* Security Section */}
            {activeSection === 'security' && (
              <div className="lg:col-span-2 space-y-6">
                {/* Provider Data Boundary */}
                <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-primary-800 dark:text-primary-300">
                        Provider Data Boundary
                      </h4>
                      <p className="text-sm text-primary-700 dark:text-primary-400 mt-1">
                        Workspace files are stored and processed on this server. Assistant providers receive
                        the prompts, context, file excerpts, tool results, and attachments needed for a chat.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Data Flow */}
                <div>
                  <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">
                    Data Flow
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Stays Local */}
                    <div className="p-4 rounded-lg border border-surface-200 dark:border-surface-700">
                      <div className="flex items-center gap-2 mb-3">
                        <HardDrive className="w-4 h-4 text-primary-500" />
                        <span className="font-medium text-surface-700 dark:text-surface-300">
                          Handled on This Server
                        </span>
                      </div>
                      <ul className="space-y-2 text-sm text-surface-600 dark:text-surface-400">
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-primary-500" />
                          Uploaded files and workspace folders
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-primary-500" />
                          Local file operations and analysis scripts
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-primary-500" />
                          Chat session history
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-primary-500" />
                          Generated reports and outputs
                        </li>
                      </ul>
                    </div>

                    {/* To Assistant Provider */}
                    <div className="p-4 rounded-lg border border-surface-200 dark:border-surface-700">
                      <div className="flex items-center gap-2 mb-3">
                        <Server className="w-4 h-4 text-primary-500" />
                        <span className="font-medium text-surface-700 dark:text-surface-300">
                          Sent to Assistant Provider
                        </span>
                      </div>
                      <ul className="space-y-2 text-sm text-surface-600 dark:text-surface-400">
                        <li className="flex items-center gap-2">
                          <Lock className="w-3 h-3 text-primary-500" />
                          User prompts and system instructions
                        </li>
                        <li className="flex items-center gap-2">
                          <Eye className="w-3 h-3 text-primary-500" />
                          Selected file paths, screenshots, and attachments
                        </li>
                        <li className="flex items-center gap-2">
                          <Shield className="w-3 h-3 text-primary-500" />
                          File excerpts or summaries read during the chat
                        </li>
                        <li className="flex items-center gap-2">
                          <Lock className="w-3 h-3 text-primary-500" />
                          Tool results needed to answer the request
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Provider Privacy */}
                <div>
                  <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">
                    Provider Privacy
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      { label: 'Provider terms', value: 'Account-specific', positive: false },
                      { label: 'Training controls', value: 'Provider-managed', positive: false },
                      { label: 'Retention controls', value: 'Provider-managed', positive: false },
                      { label: 'Auth scope', value: 'Per user', positive: true },
                      { label: 'Workspace storage', value: 'Server-side', positive: true },
                      { label: 'Generated outputs', value: 'Server-side', positive: true },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="p-3 rounded-lg bg-surface-100 dark:bg-surface-800"
                      >
                        <div className="text-xs text-surface-500 dark:text-surface-400">
                          {item.label}
                        </div>
                        <div
                          className={cn(
                            'font-medium',
                            item.positive
                              ? 'text-primary-600 dark:text-primary-400'
                              : 'text-surface-700 dark:text-surface-300'
                          )}
                        >
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Current Configuration */}
                {systemInfo && (
                  <div>
                    <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">
                      Current Configuration
                    </h4>
                    <div className="p-4 rounded-lg bg-surface-100 dark:bg-surface-800 space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <FolderOpen className="w-4 h-4 text-surface-500" />
                        <span className="text-surface-500 dark:text-surface-400">Data Directory:</span>
                        <code className="px-2 py-0.5 rounded bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300">
                          {systemInfo.dataDir || 'Not configured'}
                        </code>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* App Settings Section */}
            {activeSection === 'app' && (
              <div className="lg:col-span-2 grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div className="space-y-5">
                  <section className="rounded-lg border border-surface-200 bg-white p-4 dark:border-surface-700 dark:bg-surface-900">
                    <div className="mb-4 flex items-center justify-between gap-4">
                      <div>
                        <h4 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
                          Profile
                        </h4>
                        <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                          Local display details for this browser session.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 rounded-md bg-surface-100 px-2.5 py-1.5 text-xs text-surface-600 dark:bg-surface-800 dark:text-surface-300">
                        <User className="h-3.5 w-3.5" />
                        <span className="max-w-40 truncate">{user?.username}</span>
                      </div>
                    </div>

                    <label className="block text-xs font-medium text-surface-600 dark:text-surface-400">
                      Your Name
                    </label>
                    <Input
                      type="text"
                      value={settings.userName}
                      onChange={(e) => settings.setUserName(e.target.value)}
                      placeholder="Enter your name"
                      className="mt-1.5"
                    />
                  </section>

                  <section className="rounded-lg border border-surface-200 bg-white p-4 dark:border-surface-700 dark:bg-surface-900">
                    <button
                      onClick={() => {
                        setShowChangePassword(!showChangePassword);
                        setChangePasswordError(null);
                        setChangePasswordSuccess(false);
                      }}
                      className="flex w-full items-center justify-between gap-4 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-surface-100 text-surface-600 dark:bg-surface-800 dark:text-surface-300">
                          <Lock className="h-4 w-4" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
                            Change Password
                          </h4>
                          <p className="mt-0.5 text-xs text-surface-500 dark:text-surface-400">
                            Update the password for this account.
                          </p>
                        </div>
                      </div>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 text-surface-400 transition-transform',
                          showChangePassword && 'rotate-180'
                        )}
                      />
                    </button>

                    {showChangePassword && (
                      <div className="mt-4 border-t border-surface-200 pt-4 dark:border-surface-700">
                        <div className="grid gap-3">
                          <div>
                            <label className="block text-xs font-medium text-surface-600 dark:text-surface-400">
                              Current Password
                            </label>
                            <Input
                              type="password"
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              placeholder="Current password"
                              className="mt-1.5"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-surface-600 dark:text-surface-400">
                              New Password
                            </label>
                            <Input
                              type="password"
                              value={newPasswordSelf}
                              onChange={(e) => setNewPasswordSelf(e.target.value)}
                              placeholder="New password"
                              className="mt-1.5"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-surface-600 dark:text-surface-400">
                              Confirm Password
                            </label>
                            <Input
                              type="password"
                              value={confirmNewPassword}
                              onChange={(e) => setConfirmNewPassword(e.target.value)}
                              placeholder="Confirm password"
                              className="mt-1.5"
                            />
                          </div>
                        </div>

                        {changePasswordError && (
                          <div className="mt-3 flex items-center gap-2 rounded-md bg-red-50 p-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                            <AlertCircle className="h-4 w-4" />
                            {changePasswordError}
                          </div>
                        )}

                        {changePasswordSuccess && (
                          <div className="mt-3 flex items-center gap-2 rounded-md bg-primary-50 p-2 text-sm text-primary-600 dark:bg-primary-900/20 dark:text-primary-400">
                            <CheckCircle className="h-4 w-4" />
                            Password changed successfully.
                          </div>
                        )}

                        <div className="mt-4 flex justify-end">
                          <Button
                            onClick={handleChangePassword}
                            disabled={changePasswordLoading}
                          >
                            {changePasswordLoading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Changing...
                              </>
                            ) : (
                              'Change Password'
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </section>

                  <section className="rounded-lg border border-surface-200 bg-white p-4 dark:border-surface-700 dark:bg-surface-900">
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
                        Local Context
                      </h4>
                    </div>

                    <label className="block text-xs font-medium text-surface-600 dark:text-surface-400">
                      Timezone
                    </label>
                    <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                      Used for chat times and assistant context. Saved for your account in this browser.
                    </p>
                    <TimezoneSelect
                      value={settings.contextTimezone}
                      onChange={settings.setContextTimezone}
                    />
                  </section>
                </div>

                <div className="space-y-5">
                  <section className="rounded-lg border border-surface-200 bg-white p-4 dark:border-surface-700 dark:bg-surface-900">
                    <h4 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
                      Appearance
                    </h4>

                    <div className="mt-4">
                      <label className="block text-xs font-medium text-surface-600 dark:text-surface-400">
                        Theme
                      </label>
                      <div className="mt-2 grid grid-cols-3 overflow-hidden rounded-lg border border-surface-200 dark:border-surface-700">
                        {themeOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => settings.setTheme(option.value)}
                            aria-pressed={settings.theme === option.value}
                            className={cn(
                              'flex min-h-11 items-center justify-center gap-2 border-r border-surface-200 px-3 text-sm font-medium last:border-r-0 transition-colors dark:border-surface-700',
                              settings.theme === option.value
                                ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/25 dark:text-primary-300'
                                : 'text-surface-600 hover:bg-surface-50 dark:text-surface-300 dark:hover:bg-surface-800'
                            )}
                          >
                            {option.icon}
                            <span>{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                  </section>

                  <section className="rounded-lg border border-surface-200 bg-white p-4 dark:border-surface-700 dark:bg-surface-900">
                    <div className="mb-3 flex items-center gap-2">
                      <Keyboard className="h-4 w-4 text-surface-500" />
                      <h4 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
                        Keyboard Shortcuts
                      </h4>
                    </div>
                    <div className="space-y-2">
                      {shortcuts.map((shortcut, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between gap-4 text-sm"
                        >
                          <span className="text-surface-600 dark:text-surface-400">
                            {shortcut.description}
                          </span>
                          <span className="flex items-center gap-1">
                            {shortcut.keys.map((key, keyIndex) => (
                              <span key={keyIndex} className="flex items-center gap-1">
                                <kbd className="rounded bg-surface-100 px-1.5 py-0.5 font-mono text-xs text-surface-700 dark:bg-surface-800 dark:text-surface-300">
                                  {key}
                                </kbd>
                                {keyIndex < shortcut.keys.length - 1 && (
                                  <span className="text-surface-400">+</span>
                                )}
                              </span>
                            ))}
                          </span>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              </div>
            )}

            {/* Users Section (Admin Only) */}
            {activeSection === 'users' && isAdmin && (
              <div className="lg:col-span-2 space-y-6">
                {/* Header with Create button */}
                <div className="flex items-center justify-between">
                  <p className="text-surface-600 dark:text-surface-400">
                    Manage user accounts and access
                  </p>
                  <Button
                    onClick={() => setShowCreateUser(true)}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Create User
                  </Button>
                </div>

                {/* Create User Form */}
                {showCreateUser && (
                  <div className="p-4 rounded-lg border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800">
                    <h4 className="font-medium text-surface-700 dark:text-surface-300 mb-4">
                      Create New User
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">
                          Username
                        </label>
                        <Input
                          type="text"
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          placeholder="Enter username"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">
                          Password
                        </label>
                        <Input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter password (min 8 characters)"
                          className="w-full"
                        />
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-lg border border-surface-200 dark:border-surface-700 bg-surface-100 dark:bg-surface-900">
                        <input
                          type="checkbox"
                          id="isAdmin"
                          checked={newUserIsAdmin}
                          onChange={(e) => setNewUserIsAdmin(e.target.checked)}
                          className="w-4 h-4 rounded border-surface-400 dark:border-surface-500 text-primary-500 focus:ring-primary-500 focus:ring-offset-0 dark:bg-surface-700"
                        />
                        <div>
                          <label htmlFor="isAdmin" className="text-sm font-medium text-surface-700 dark:text-surface-200 cursor-pointer">
                            Admin privileges
                          </label>
                          <p className="text-xs text-surface-500 dark:text-surface-400">
                            Terminal access, user management, system settings
                          </p>
                        </div>
                      </div>
                      {createUserError && (
                        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                          <AlertCircle className="w-4 h-4" />
                          {createUserError}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          onClick={handleCreateUser}
                          disabled={createUserLoading || !newUsername.trim() || !newPassword}
                        >
                          {createUserLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Create User
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowCreateUser(false);
                            setNewUsername('');
                            setNewPassword('');
                            setNewUserIsAdmin(false);
                            setCreateUserError(null);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Users List */}
                <div>
                  <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">
                    All Users
                  </h4>
                  {usersLoading ? (
                    <div className="flex items-center justify-center p-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                    </div>
                  ) : usersError ? (
                    <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
                      {usersError}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {users.map((u) => {
                        const isExpanded = expandedUserId === u.id;
                        const isSelf = user?.id === u.id;

                        return (
                          <div
                            key={u.id}
                            className="rounded-lg border border-surface-200 dark:border-surface-700 overflow-hidden"
                          >
                            {/* Collapsed Header - Click to expand */}
                            <button
                              onClick={() => setExpandedUserId(isExpanded ? null : u.id)}
                              className="w-full flex items-center justify-between p-3 hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className={cn(
                                  'w-8 h-8 rounded-full flex items-center justify-center',
                                  u.isAdmin
                                    ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400'
                                    : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400'
                                )}>
                                  <User className="w-4 h-4" />
                                </div>
                                <div className="text-left">
                                  <div className="font-medium text-surface-700 dark:text-surface-300">
                                    {u.username}
                                    {u.isAdmin && (
                                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400">
                                        Admin
                                      </span>
                                    )}
                                    {isSelf && (
                                      <span className="ml-2 text-xs text-surface-500">(you)</span>
                                    )}
                                  </div>
                                  <div className="text-xs text-surface-500 dark:text-surface-400">
                                    Created {new Date(u.createdAt).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                              <div className={cn(
                                'text-surface-400 transition-transform',
                                isExpanded && 'rotate-180'
                              )}>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </button>

                            {/* Expanded Content */}
                            {isExpanded && (
                              <div className="border-t border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/30 p-4 space-y-4">
                                {/* Reset Password - not for self */}
                                {!isSelf && (
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm font-medium text-surface-700 dark:text-surface-300">
                                      <Key className="w-4 h-4" />
                                      Reset Password
                                    </div>
                                    <p className="text-xs text-surface-500 dark:text-surface-400">
                                      Set a new password for this user
                                    </p>
                                    {resetPasswordUserId === u.id ? (
                                      <div className="space-y-2">
                                        <Input
                                          type="password"
                                          value={resetPassword}
                                          onChange={(e) => setResetPassword(e.target.value)}
                                          placeholder="New password (min 8 characters)"
                                          className="w-full"
                                        />
                                        {resetPasswordError && (
                                          <p className="text-xs text-red-500">{resetPasswordError}</p>
                                        )}
                                        <div className="flex gap-2">
                                          <Button
                                            size="sm"
                                            onClick={() => handleResetPassword(u.id)}
                                            disabled={resetPasswordLoading || !resetPassword || resetPassword.length < 8}
                                          >
                                            {resetPasswordLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                                            Save Password
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => {
                                              setResetPasswordUserId(null);
                                              setResetPassword('');
                                              setResetPasswordError(null);
                                            }}
                                          >
                                            Cancel
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setResetPasswordUserId(u.id)}
                                      >
                                        Change Password
                                      </Button>
                                    )}
                                  </div>
                                )}

                                {/* Reset Claude Credentials */}
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 text-sm font-medium text-surface-700 dark:text-surface-300">
                                    <RotateCw className="w-4 h-4" />
                                    Reset Claude Authentication
                                  </div>
                                  <p className="text-xs text-surface-500 dark:text-surface-400">
                                    Clear stored Claude credentials. User will need to re-authenticate with Claude on next login.
                                  </p>
                                  {resetClaudeCredsUserId === u.id ? (
                                    <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                      <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                      <span className="text-sm text-amber-700 dark:text-amber-300">Confirm reset?</span>
                                      <div className="flex gap-2 ml-auto">
                                        <Button
                                          size="sm"
                                          onClick={() => handleResetClaudeCredentials(u.id)}
                                          disabled={resetClaudeCredsLoading}
                                        >
                                          {resetClaudeCredsLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                                          Yes, Reset
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => setResetClaudeCredsUserId(null)}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setResetClaudeCredsUserId(u.id)}
                                    >
                                      Reset Claude Auth
                                    </Button>
                                  )}
                                </div>

                                {/* Delete User - not for self */}
                                {!isSelf && (
                                  <div className="space-y-2 pt-4 border-t border-surface-200 dark:border-surface-700">
                                    <div className="flex items-center gap-2 text-sm font-medium text-red-600 dark:text-red-400">
                                      <Trash2 className="w-4 h-4" />
                                      Delete User
                                    </div>
                                    <p className="text-xs text-surface-500 dark:text-surface-400">
                                      Permanently remove this user and all their data. This cannot be undone.
                                    </p>
                                    {deleteConfirmUserId === u.id ? (
                                      <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                                        <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                                        <span className="text-sm text-red-700 dark:text-red-300">This will delete all user data!</span>
                                        <div className="flex gap-2 ml-auto">
                                          <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => handleDeleteUser(u.id)}
                                          >
                                            Delete Forever
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => setDeleteConfirmUserId(null)}
                                          >
                                            Cancel
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20"
                                        onClick={() => setDeleteConfirmUserId(u.id)}
                                      >
                                        Delete User
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeSection === 'training' && isAdmin && legacyTrainingMemoryEnabled && (
              <div className="lg:col-span-2 space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-1">
                      Training Memory
                    </h3>
                    <p className="text-sm text-surface-500 dark:text-surface-400">
                      Maintain tenant-local operating guidance for this instance.
                    </p>
                  </div>
                  <Button variant="outline" onClick={handleMaterializeTrainingMemory}>
                    Materialize Bundle
                  </Button>
                </div>

                {trainingError && (
                  <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
                    <AlertCircle className="h-4 w-4" />
                    {trainingError}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[360px_1fr]">
                  <div className="space-y-4 rounded-lg border border-surface-200 p-4 dark:border-surface-700">
                    <div>
                      <h4 className="font-medium text-surface-800 dark:text-surface-100">
                        Add Operating Guidance
                      </h4>
                      <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
                        Use this for roles, workflows, policies, definitions, and preferences.
                      </p>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-surface-600 dark:text-surface-400">
                        Title
                      </label>
                      <Input
                        value={trainingTitle}
                        onChange={(e) => setTrainingTitle(e.target.value)}
                        placeholder="Traffic Manager role"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-surface-600 dark:text-surface-400">
                        Category
                      </label>
                      <select
                        value={trainingCategory}
                        onChange={(e) => setTrainingCategory(e.target.value as TrainingMemoryCategory)}
                        className="w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 dark:border-surface-700 dark:bg-surface-900 dark:text-surface-100"
                      >
                        <option value="role">Role</option>
                        <option value="workflow">Workflow</option>
                        <option value="policy">Policy</option>
                        <option value="definition">Definition</option>
                        <option value="preference">Preference</option>
                        <option value="note">Note</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-surface-600 dark:text-surface-400">
                        Guidance
                      </label>
                      <textarea
                        value={trainingContent}
                        onChange={(e) => setTrainingContent(e.target.value)}
                        rows={6}
                        className="w-full resize-none rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 dark:border-surface-700 dark:bg-surface-900 dark:text-surface-100"
                        placeholder="Describe the tenant operating knowledge."
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-surface-600 dark:text-surface-400">
                        Applies When
                      </label>
                      <textarea
                        value={trainingAppliesWhen}
                        onChange={(e) => setTrainingAppliesWhen(e.target.value)}
                        rows={3}
                        className="w-full resize-none rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 dark:border-surface-700 dark:bg-surface-900 dark:text-surface-100"
                        placeholder="One condition per line"
                      />
                    </div>

                    <Button
                      onClick={handleCreateTrainingMemory}
                      disabled={trainingSaving || !trainingTitle.trim() || !trainingContent.trim()}
                      className="w-full"
                    >
                      {trainingSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Add Active Memory
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      {(['active', 'proposed', 'archived'] as const).map((status) => (
                        <div key={status} className="rounded-lg bg-surface-100 p-3 dark:bg-surface-800">
                          <div className="text-xs uppercase tracking-wide text-surface-500 dark:text-surface-400">
                            {status}
                          </div>
                          <div className="mt-1 text-2xl font-semibold text-surface-900 dark:text-surface-100">
                            {trainingMemories.filter((memory) => memory.status === status).length}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="rounded-lg border border-surface-200 dark:border-surface-700">
                      <div className="flex items-center justify-between border-b border-surface-200 px-4 py-3 dark:border-surface-700">
                        <h4 className="font-medium text-surface-800 dark:text-surface-100">
                          Memory Records
                        </h4>
                        <Button variant="ghost" size="sm" onClick={fetchTrainingMemory} disabled={trainingLoading}>
                          {trainingLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        </Button>
                      </div>

                      {trainingLoading ? (
                        <div className="flex items-center justify-center p-10">
                          <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
                        </div>
                      ) : trainingMemories.length === 0 ? (
                        <div className="p-8 text-center text-sm text-surface-500 dark:text-surface-400">
                          No Training Memory has been added for this tenant.
                        </div>
                      ) : (
                        <div className="divide-y divide-surface-200 dark:divide-surface-700">
                          {trainingMemories.map((memory) => (
                            <div key={memory.id} className="p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <h5 className="font-medium text-surface-900 dark:text-surface-100">
                                      {memory.title}
                                    </h5>
                                    <span className="rounded-full bg-surface-100 px-2 py-0.5 text-xs text-surface-600 dark:bg-surface-700 dark:text-surface-300">
                                      {memory.category}
                                    </span>
                                    <span className={cn(
                                      'rounded-full px-2 py-0.5 text-xs',
                                      memory.status === 'active' && 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300',
                                      memory.status === 'proposed' && 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
                                      memory.status === 'archived' && 'bg-surface-100 text-surface-600 dark:bg-surface-700 dark:text-surface-300'
                                    )}>
                                      {memory.status}
                                    </span>
                                  </div>
                                  <p className="mt-2 whitespace-pre-wrap text-sm text-surface-600 dark:text-surface-300">
                                    {memory.content}
                                  </p>
                                  {memory.appliesWhen.length > 0 && (
                                    <div className="mt-3 text-xs text-surface-500 dark:text-surface-400">
                                      Applies when: {memory.appliesWhen.join('; ')}
                                    </div>
                                  )}
                                  <div className="mt-2 text-xs text-surface-400">
                                    Added by {memory.createdBy.username || 'unknown'} on {new Date(memory.createdAt).toLocaleString()}
                                  </div>
                                </div>
                                <div className="flex shrink-0 gap-2">
                                  {memory.status !== 'active' && (
                                    <Button size="sm" variant="outline" onClick={() => handleTrainingMemoryStatus(memory.id, 'active')}>
                                      Activate
                                    </Button>
                                  )}
                                  {memory.status !== 'archived' && (
                                    <Button size="sm" variant="outline" onClick={() => handleTrainingMemoryStatus(memory.id, 'archived')}>
                                      Archive
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {trainingEvents.length > 0 && (
                      <div className="rounded-lg border border-surface-200 p-4 dark:border-surface-700">
                        <h4 className="mb-3 font-medium text-surface-800 dark:text-surface-100">
                          Recent Audit Events
                        </h4>
                        <ul className="space-y-2 text-sm text-surface-600 dark:text-surface-300">
                          {trainingEvents.map((event, index) => (
                            <li key={`${event}-${index}`} className="flex gap-2">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                              <span>{event}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'training' && (
              <div className="lg:col-span-2">
                <LearningPanel />
              </div>
            )}

            {/* Skills Section (Admin Only) */}
            {activeSection === 'skills' && isAdmin && (
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-1">
                    Skills
                  </h3>
                  <p className="text-sm text-surface-500 dark:text-surface-400">
                    Upload shared skills that users can install to their accounts.
                  </p>
                </div>

                {/* Upload Area */}
                <div className="border-2 border-dashed border-surface-300 dark:border-surface-600 rounded-lg p-6">
                  <div className="text-center">
                    <Upload className="w-8 h-8 mx-auto text-surface-400 mb-2" />
                    <p className="text-sm text-surface-600 dark:text-surface-400 mb-2">
                      Select a skill folder to upload
                    </p>
                    <p className="text-xs text-surface-500 dark:text-surface-500 mb-4">
                      Folder must contain a SKILL.md file.
                    </p>
                    <input
                      type="file"
                      /* @ts-expect-error webkitdirectory is non-standard but widely supported */
                      webkitdirectory=""
                      directory=""
                      className="hidden"
                      id="skill-upload"
                      onChange={async (e) => {
                        const files = e.target.files;
                        if (!files || files.length === 0) return;

                        setUploadingSkill(true);
                        setUploadError(null);

                        try {
                          await api.admin.uploadSkillFolder(files);
                          // Refresh skills list
                          const response = await api.skills.listShared();
                          setSharedSkills(response.skills);
                        } catch (err) {
                          setUploadError(err instanceof Error ? err.message : 'Upload failed');
                        } finally {
                          setUploadingSkill(false);
                          e.target.value = '';
                        }
                      }}
                      disabled={uploadingSkill}
                    />
                    <label htmlFor="skill-upload">
                      <Button
                        variant="outline"
                        disabled={uploadingSkill}
                        className="cursor-pointer"
                        asChild
                      >
                        <span>
                          {uploadingSkill ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Choose Folder
                            </>
                          )}
                        </span>
                      </Button>
                    </label>
                  </div>
                  {uploadError && (
                    <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {uploadError}
                      </p>
                    </div>
                  )}
                </div>

                {/* Skills List */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300">
                      Shared Items ({sharedSkills.length})
                    </h4>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        setSkillsLoading(true);
                        setSkillsError(null);
                        try {
                          const response = await api.skills.listShared();
                          setSharedSkills(response.skills);
                        } catch (err) {
                          setSkillsError(err instanceof Error ? err.message : 'Failed to load skills');
                        } finally {
                          setSkillsLoading(false);
                        }
                      }}
                      disabled={skillsLoading}
                    >
                      {skillsLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Refresh'
                      )}
                    </Button>
                  </div>

                  {skillsError && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg mb-3">
                      <p className="text-sm text-red-600 dark:text-red-400">{skillsError}</p>
                    </div>
                  )}

                  {skillsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-surface-400" />
                    </div>
                  ) : sharedSkills.length === 0 ? (
                    <div className="text-center py-8 text-surface-500 dark:text-surface-400">
                      <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No shared skills or workflows yet</p>
                      <p className="text-sm">Upload a folder to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {sharedSkills.map((skill) => {
                        const description = skill.description || 'No description';
                        const isExpanded = expandedSkillDescriptions.has(skill.name);
                        const canExpand = description.length > 120 || description.includes('\n');

                        return (
                          <div
                            key={skill.name}
                            className="w-full rounded-lg border border-surface-200 bg-surface-50 p-3 dark:border-surface-700 dark:bg-surface-800/50"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="break-words font-medium text-surface-900 dark:text-white">
                                    {skill.title || skill.name}
                                  </p>
                                  {skill.shareable === false && (
                                    <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" title="Tenant-private — excluded from cross-tenant marketplace bundle">
                                      tenant-private
                                    </span>
                                  )}
                                </div>
                                <p className="mt-0.5 break-all font-mono text-xs text-surface-500 dark:text-surface-500">
                                  {skill.name}
                                </p>
                              </div>
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant={installedSkillNames.has(skill.name) ? 'outline' : 'default'}
                                  onClick={() => handleInstallSkillForMe(skill.name)}
                                  disabled={installingSkill === skill.name}
                                >
                                  {installingSkill === skill.name ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : installedSkillNames.has(skill.name) ? (
                                    <>
                                      <RotateCw className="w-4 h-4 mr-2" />
                                      Update my copy
                                    </>
                                  ) : (
                                    <>
                                      <FolderOpen className="w-4 h-4 mr-2" />
                                      Install for me
                                    </>
                                  )}
                                </Button>
                                {deleteSkillConfirm === skill.name ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-surface-500">Delete?</span>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={async () => {
                                        try {
                                          await api.admin.deleteSkill(skill.name);
                                          setSharedSkills(sharedSkills.filter(s => s.name !== skill.name));
                                        } catch (err) {
                                          setSkillsError(err instanceof Error ? err.message : 'Delete failed');
                                        }
                                        setDeleteSkillConfirm(null);
                                      }}
                                    >
                                      Yes
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setDeleteSkillConfirm(null)}
                                    >
                                      No
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setDeleteSkillConfirm(skill.name)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>

                            <div className="mt-3 border-t border-surface-200 pt-3 dark:border-surface-700">
                              <div
                                className={cn(
                                  'whitespace-pre-wrap break-words text-sm leading-6 text-surface-600 dark:text-surface-300',
                                  !isExpanded && canExpand && 'max-h-12 overflow-hidden'
                                )}
                              >
                                {description}
                              </div>
                              {canExpand && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => toggleSkillDescription(skill.name)}
                                  className="mt-2 h-8 px-2 text-surface-600 dark:text-surface-300"
                                >
                                  <ChevronDown
                                    className={cn(
                                      'mr-1 h-4 w-4 transition-transform',
                                      isExpanded && 'rotate-180'
                                    )}
                                  />
                                  {isExpanded ? 'Show less' : 'Show more'}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* About Section */}
            {activeSection === 'about' && (
              <div className="space-y-6">
                {/* Logo and Version */}
                <div className="text-center">
                  <div className="inline-block mb-4">
                    <TelcoOSLogo className="w-20 h-20 rounded-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-surface-900 dark:text-white">
                    TelcoOS
                  </h3>
                  <p className="text-surface-500 dark:text-surface-400">
                    Version 0.1.0
                  </p>
                </div>

                {/* Description */}
                <p className="text-center text-surface-600 dark:text-surface-400">
                  Cloud-hosted telecom data analytics platform powered by Claude Code and Codex.
                </p>

                {/* Links */}
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      onClose();
                      navigate('/users-guide');
                    }}
                    className="w-full flex items-center justify-between p-3 rounded-lg border border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
                  >
                    <span className="text-surface-700 dark:text-surface-300">User Guide</span>
                    <BookOpen className="w-4 h-4 text-surface-400" />
                  </button>
                  <a
                    href="https://status.claude.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg border border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
                  >
                    <span className="text-surface-700 dark:text-surface-300">Claude Status</span>
                    <ExternalLink className="w-4 h-4 text-surface-400" />
                  </a>
                  <a
                    href="https://platform.claude.com/docs/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg border border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
                  >
                    <span className="text-surface-700 dark:text-surface-300">Claude Documentation</span>
                    <ExternalLink className="w-4 h-4 text-surface-400" />
                  </a>
                  <a
                    href="https://status.openai.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg border border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
                  >
                    <span className="text-surface-700 dark:text-surface-300">Codex Status</span>
                    <ExternalLink className="w-4 h-4 text-surface-400" />
                  </a>
                  <a
                    href="https://developers.openai.com/codex/cli"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg border border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
                  >
                    <span className="text-surface-700 dark:text-surface-300">Codex Documentation</span>
                    <ExternalLink className="w-4 h-4 text-surface-400" />
                  </a>
                </div>

                {/* Attribution */}
                <div className="text-center text-sm text-surface-500 dark:text-surface-400 pt-4 border-t border-surface-200 dark:border-surface-700">
                  Built with React, Express, Claude Code, and Codex CLI
                </div>
              </div>
            )}
            </div>{/* End grid */}
          </div>
        </div>
      </div>
    </>
  );
}

export default SettingsPanel;
